import { ReactNode, useState } from 'react';

import { commonTranslationKeys, createTranslatedText, Translate } from '@dreryk/react-common';
import {
   MessageType,
   ModalCancelButton,
   ModalCloseButton,
   ModalConfirmButton,
   ModalWidth,
   ShowModalEventInterceptor,
} from '@dreryk/react-components';

import { useAccountAuthorizationModal } from '~libs.desktop.shared.account';

import { sentPrescriptionPackagesTranslationKeys } from '../../../../../translations';
import { PrescriptionStatus } from '../../draftPrescriptions/common/PrescriptionStatus';
import { PrescriptionStatusBoxPortal } from '../../prescription/status/PrescriptionStatusBoxPortal';

import { SentPrescriptionAnnulmentCommand } from './SentPrescriptionAnnulmentCommand';

type SentPrescriptionAnnulmentConfirmationEventInterceptorProps = {
   readonly children: ReactNode;
   readonly prescriptionId: string;
   readonly visitId: number;
};

export const SentPrescriptionAnnulmentConfirmationEventInterceptor = (
   props: SentPrescriptionAnnulmentConfirmationEventInterceptorProps
): JSX.Element => {
   const { openAccountAuthorizationModal, renderAccountAuthorizationnModal } = useAccountAuthorizationModal();
   const [errorMessage, setErrorMessage] = useState<string | null>(null);

   return (
      <>
         <ShowModalEventInterceptor
            shouldShowModal
            modalWidth={ModalWidth.NORMAL}
            testId={'sentPrescriptionAnnulmentConfirmation'}
            title={sentPrescriptionPackagesTranslationKeys.options.annul.title}
            content={({ resolve, reject }): JSX.Element => {
               return (
                  <>
                     <SentPrescriptionAnnulmentCommand
                        prescriptionId={props.prescriptionId}
                        visitId={props.visitId}
                        onAuthenticationRequiredFault={openAccountAuthorizationModal}
                        onPrescriptionOperationFault={(message) => {
                           setErrorMessage(message);
                           reject();
                        }}
                     >
                        {({ sentPrescriptionAnnulment }) => (
                           <>
                              {renderAccountAuthorizationnModal({
                                 onAuthorization: sentPrescriptionAnnulment,
                              })}
                              <ModalConfirmButton
                                 blockingOperation={
                                    sentPrescriptionPackagesTranslationKeys.options.annul.blockingOperation
                                 }
                                 label={commonTranslationKeys.remove}
                                 onClick={async () => {
                                    await sentPrescriptionAnnulment();
                                    resolve();
                                 }}
                              />
                           </>
                        )}
                     </SentPrescriptionAnnulmentCommand>
                     <Translate>{sentPrescriptionPackagesTranslationKeys.options.annul.confirmationContent}</Translate>
                     <ModalCancelButton label={commonTranslationKeys.cancel} onClick={reject} />
                     <ModalCloseButton onClick={reject} />
                  </>
               );
            }}
            footerItemsHidden
         >
            {props.children}
         </ShowModalEventInterceptor>

         {errorMessage ? (
            <PrescriptionStatusBoxPortal prescriptionId={props.prescriptionId}>
               <PrescriptionStatus
                  testId={'sentPrescriptionAnnulmentError'}
                  type={MessageType.NEGATIVE}
                  label={createTranslatedText(errorMessage)}
               />
            </PrescriptionStatusBoxPortal>
         ) : null}
      </>
   );
};
